import {FC, useEffect, useState, ReactNode} from 'react'
import type React from 'react'
import ReactApexChart from 'react-apexcharts'
import {useAuth} from '../../modules/auth'
import {api, AnalyticsSummary, TimePoint} from '../../services/api'

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'https://api.aiiplan.com/api').replace('/api', '')

interface DashUser {
  id: string
  firstName: string
  lastName: string
  email: string
  photo?: string
  createdAt: string
}

interface DashBooking {
  id: string
  providerName: string
  status: string
  subtotal: number
  depositAmount: number
  createdAt: string
  event?: {eventName?: string}
  user?: {firstName: string; lastName: string}
}

function resolvePhoto(photo?: string): string | null {
  if (!photo) return null
  return photo.startsWith('http') || photo.startsWith('data:') ? photo : `${API_ORIGIN}${photo}`
}

function userInitials(u: DashUser) {
  return ((u.firstName?.[0] ?? '') + (u.lastName?.[0] ?? '')).toUpperCase() || '?'
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1)   return 'just now'
  if (mins < 60)  return `${mins} min${mins > 1 ? 's' : ''} ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)   return `${hrs} hour${hrs > 1 ? 's' : ''} ago`
  const days = Math.floor(hrs / 24)
  return `${days} day${days > 1 ? 's' : ''} ago`
}

const fmtMoney = (n: number) => `$${(n || 0).toLocaleString()}`
const fmtNum = (n: number) => (n || 0).toLocaleString()

const AVATAR_COLORS = ['#3b82f6', '#6366f1', '#8b5cf6', '#ec4899', '#f97316', '#22c55e']
function avatarBg(name: string) {
  let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

const UserAvatar: FC<{user: DashUser; size?: number}> = ({user, size = 44}) => {
  const [failed, setFailed] = useState(false)
  const url = !failed ? resolvePhoto(user.photo) : null
  if (url) {
    return (
      <img
        src={url}
        alt={user.firstName}
        onError={() => setFailed(true)}
        style={{width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0}}
      />
    )
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: avatarBg(user.firstName + user.lastName),
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontWeight: 700, fontSize: size * 0.3,
    }}>
      {userInitials(user)}
    </div>
  )
}

// ── Stat-card icons ──────────────────────────────────────────────────────────
const usersIcon = (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2'/><circle cx='9' cy='7' r='4'/>
    <path d='M23 21v-2a4 4 0 0 0-3-3.87'/><path d='M16 3.13a4 4 0 0 1 0 7.75'/>
  </svg>
)
const eventIcon = (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <rect x='3' y='4' width='18' height='18' rx='2'/><line x1='16' y1='2' x2='16' y2='6'/>
    <line x1='8' y1='2' x2='8' y2='6'/><line x1='3' y1='10' x2='21' y2='10'/>
  </svg>
)
const moneyIcon = (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <line x1='12' y1='1' x2='12' y2='23'/><path d='M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6'/>
  </svg>
)
const bookingIcon = (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <polyline points='22 7 13.5 15.5 8.5 10.5 2 17'/><polyline points='16 7 22 7 22 13'/>
  </svg>
)

interface StatCard {
  label: string
  value: string
  sub?: string
  bg: string
  iconColor: string
  icon: ReactNode
}

function buildStats(s: AnalyticsSummary | null): StatCard[] {
  return [
    {label: 'Total Users',  value: s ? fmtNum(s.totalUsers) : '—',     sub: s ? `+${fmtNum(s.newUsers30d)} this month` : undefined, bg: '#eff6ff', iconColor: '#3b82f6', icon: usersIcon},
    {label: 'Total Events', value: s ? fmtNum(s.totalEvents) : '—',                                                                   bg: '#f0fdf4', iconColor: '#22c55e', icon: eventIcon},
    {label: 'Total Revenue',value: s ? fmtMoney(s.totalRevenue) : '—', sub: s ? `${fmtMoney(s.revenue30d)} in 30d` : undefined,        bg: '#faf5ff', iconColor: '#a855f7', icon: moneyIcon},
    {label: 'Bookings',     value: s ? fmtNum(s.totalBookings) : '—',  sub: s ? `${fmtNum(s.activeProviders)} active providers` : undefined, bg: '#fff7ed', iconColor: '#f97316', icon: bookingIcon},
  ]
}

const revenueOptions = (cats: string[]): ApexCharts.ApexOptions => ({
  chart: {type: 'bar', toolbar: {show: false}, fontFamily: 'inherit'},
  plotOptions: {bar: {borderRadius: 4, columnWidth: '55%'}},
  colors: ['#3b82f6'],
  dataLabels: {enabled: false},
  xaxis: {categories: cats, axisBorder: {show: false}, axisTicks: {show: false}},
  yaxis: {labels: {formatter: (v) => `${(v / 1000).toFixed(0)}k`}},
  grid: {borderColor: '#f1f5f9', strokeDashArray: 4},
  tooltip: {y: {formatter: (v) => `$${v.toLocaleString()}`}},
})

const growthOptions = (cats: string[]): ApexCharts.ApexOptions => ({
  chart: {type: 'line', toolbar: {show: false}, fontFamily: 'inherit'},
  stroke: {curve: 'smooth', width: 3},
  colors: ['#22c55e'],
  markers: {size: 4, colors: ['#22c55e'], strokeColors: '#fff', strokeWidth: 2},
  dataLabels: {enabled: false},
  xaxis: {categories: cats, axisBorder: {show: false}, axisTicks: {show: false}},
  yaxis: {labels: {formatter: (v) => `${v.toFixed(0)}`}},
  grid: {borderColor: '#f1f5f9', strokeDashArray: 4},
  tooltip: {y: {formatter: (v) => v.toLocaleString()}},
})

const statusStyle = (s: string): React.CSSProperties => {
  const base: React.CSSProperties = {
    display: 'inline-block', padding: '2px 10px', borderRadius: 20,
    fontSize: 12, fontWeight: 500,
  }
  if (s === 'completed' || s === 'accepted') return {...base, background: '#dcfce7', color: '#16a34a'}
  if (s === 'pending')                        return {...base, background: '#fef9c3', color: '#a16207'}
  if (s === 'cancelled' || s === 'rejected')  return {...base, background: '#fee2e2', color: '#dc2626'}
  return {...base, background: '#f1f5f9', color: '#64748b'}
}

const CARD: React.CSSProperties = {
  background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
}

const AdminDashboard: FC = () => {
  const {currentUser} = useAuth()
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null)
  const [revenue, setRevenue] = useState<TimePoint[]>([])
  const [growth, setGrowth] = useState<TimePoint[]>([])
  const [recentUsers, setRecentUsers] = useState<DashUser[]>([])
  const [recentBookings, setRecentBookings] = useState<DashBooking[]>([])
  const [commission, setCommission] = useState<{rate: number; available: number; upcoming: number; total: number} | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    Promise.all([
      api.getAnalyticsSummary(),
      api.getRevenueSeries('12m'),
      api.getUserGrowthSeries('12m'),
      api.getUsersPaged(1, 5, ''),
      api.getBookingsPaged(1, 5, ''),
      api.getCommissionEarnings(),
    ])
      .then(([s, rev, grw, users, bookings, comm]) => {
        if (!alive) return
        setSummary(s)
        setRevenue(rev.points)
        setGrowth(grw.points)
        setRecentUsers(users.items as DashUser[])
        setRecentBookings(bookings.items as DashBooking[])
        setCommission(comm)
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [])

  const stats = buildStats(summary)

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom: 28}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Dashboard</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>
          Welcome back, {currentUser?.first_name}! Here's what's happening on the platform.
        </p>
      </div>

      {/* Stat cards */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 24}}>
        {stats.map((s) => (
          <div key={s.label} style={{...CARD, padding: '20px 20px'}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16}}>
              <div style={{width: 44, height: 44, borderRadius: 10, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: s.iconColor}}>
                {s.icon}
              </div>
            </div>
            <div style={{fontSize: 26, fontWeight: 700, color: '#0f172a', marginBottom: 4}}>{s.value}</div>
            <div style={{fontSize: 13, color: '#94a3b8'}}>{s.label}</div>
            {s.sub ? <div style={{fontSize: 12, color: '#22c55e', fontWeight: 600, marginTop: 6}}>{s.sub}</div> : null}
          </div>
        ))}
      </div>

      {/* Platform commission (service fee) earnings */}
      <div style={{...CARD, padding: '20px 20px', marginBottom: 24}}>
        <div style={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 16}}>
          <h3 style={{fontSize: 15, fontWeight: 600, color: '#0f172a', margin: 0}}>Platform Commission <span style={{color: '#94a3b8', fontWeight: 500, fontSize: 13}}>· service fee earned</span></h3>
          <span style={{fontSize: 12, color: '#64748b'}}>Rate: {commission ? `${Math.round(commission.rate * 100)}%` : '—'}</span>
        </div>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16}}>
          {[
            {label: 'Available', hint: 'events already ended', value: commission?.available, color: '#16a34a'},
            {label: 'Upcoming', hint: 'events still to come', value: commission?.upcoming, color: '#ca8a04'},
            {label: 'Total', hint: 'all-time', value: commission?.total, color: '#0f172a'},
          ].map((c) => (
            <div key={c.label} style={{border: '1px solid #e2e8f0', borderRadius: 10, padding: 16}}>
              <div style={{fontSize: 13, color: '#94a3b8', marginBottom: 4}}>{c.label}</div>
              <div style={{fontSize: 24, fontWeight: 700, color: c.color}}>{c.value == null ? '—' : fmtMoney(c.value)}</div>
              <div style={{fontSize: 12, color: '#94a3b8', marginTop: 4}}>{c.hint}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Charts */}
      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20}}>
        <div style={{...CARD, padding: '20px 20px'}}>
          <h3 style={{fontSize: 15, fontWeight: 600, color: '#0f172a', margin: '0 0 16px'}}>Revenue Overview <span style={{color: '#94a3b8', fontWeight: 500, fontSize: 13}}>· last 12 months</span></h3>
          {loading ? (
            <div style={{height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8'}}><span className='spinner-border spinner-border-sm me-2' /> Loading…</div>
          ) : (
            <ReactApexChart type='bar' series={[{name: 'Revenue', data: revenue.map((p) => p.value)}]} options={revenueOptions(revenue.map((p) => p.label))} height={280} />
          )}
        </div>

        <div style={{...CARD, padding: '20px 20px'}}>
          <h3 style={{fontSize: 15, fontWeight: 600, color: '#0f172a', margin: '0 0 16px'}}>User Growth <span style={{color: '#94a3b8', fontWeight: 500, fontSize: 13}}>· last 12 months</span></h3>
          {loading ? (
            <div style={{height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8'}}><span className='spinner-border spinner-border-sm me-2' /> Loading…</div>
          ) : (
            <ReactApexChart type='line' series={[{name: 'New Users', data: growth.map((p) => p.value)}]} options={growthOptions(growth.map((p) => p.label))} height={280} />
          )}
        </div>
      </div>

      {/* Recent Signups + Recent Bookings */}
      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20}}>

        {/* Recent Signups */}
        <div style={{...CARD, overflow: 'hidden'}}>
          <div style={{padding: '20px 20px 16px', borderBottom: '1px solid #f1f5f9'}}>
            <h3 style={{fontSize: 15, fontWeight: 600, color: '#0f172a', margin: 0}}>Recent Signups</h3>
          </div>
          {!loading && recentUsers.length === 0 && (
            <div style={{padding: '24px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 13}}>No signups yet</div>
          )}
          {recentUsers.map((u, i) => (
            <div key={u.id} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: i < recentUsers.length - 1 ? '1px solid #f1f5f9' : 'none'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                <UserAvatar user={u} size={44} />
                <div>
                  <div style={{fontSize: 14, fontWeight: 600, color: '#0f172a'}}>{u.firstName} {u.lastName}</div>
                  <div style={{fontSize: 12, color: '#94a3b8'}}>{u.email}</div>
                </div>
              </div>
              <div style={{fontSize: 13, color: '#94a3b8', whiteSpace: 'nowrap'}}>{timeAgo(u.createdAt)}</div>
            </div>
          ))}
        </div>

        {/* Recent Bookings */}
        <div style={{...CARD, overflow: 'hidden'}}>
          <div style={{padding: '20px 20px 16px', borderBottom: '1px solid #f1f5f9'}}>
            <h3 style={{fontSize: 15, fontWeight: 600, color: '#0f172a', margin: 0}}>Recent Bookings</h3>
          </div>
          {!loading && recentBookings.length === 0 && (
            <div style={{padding: '24px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 13}}>No bookings yet</div>
          )}
          {recentBookings.map((b, i) => {
            const who = b.user ? `${b.user.firstName} ${b.user.lastName}`.trim() : '—'
            const amount = b.subtotal || b.depositAmount || 0
            return (
              <div key={b.id} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: i < recentBookings.length - 1 ? '1px solid #f1f5f9' : 'none'}}>
                <div>
                  <div style={{fontSize: 14, fontWeight: 600, color: '#0f172a'}}>{b.event?.eventName || b.providerName || 'Booking'}</div>
                  <div style={{fontSize: 12, color: '#94a3b8', marginTop: 2}}>{who}</div>
                </div>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4}}>
                  <div style={{fontSize: 15, fontWeight: 700, color: '#0f172a'}}>{fmtMoney(amount)}</div>
                  <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                    <span style={statusStyle(b.status)}>{b.status}</span>
                    <span style={{fontSize: 12, color: '#94a3b8'}}>{timeAgo(b.createdAt)}</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

      </div>
    </div>
  )
}

const AdminDashboardWrapper: FC = () => <AdminDashboard />

export {AdminDashboardWrapper}
